(() => {
  const sectionSelector = '[data-event-calendar]';
  const eventSelector = '[data-event-calendar-event]';

  const monthFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'long',
    year: 'numeric'
  });

  const isoDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const monthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

  const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

  const addMonths = (date, amount) => new Date(date.getFullYear(), date.getMonth() + amount, 1);

  const compareMonths = (a, b) => {
    const aValue = a.getFullYear() * 12 + a.getMonth();
    const bValue = b.getFullYear() * 12 + b.getMonth();

    return aValue - bValue;
  };

  class EventCalendar {
    constructor(section) {
      this.section = section;
      this.grid = section.querySelector('[data-event-calendar-grid]');
      this.monthTitle = section.querySelector('[data-event-calendar-month]');
      this.prevButton = section.querySelector('[data-event-calendar-prev]');
      this.nextButton = section.querySelector('[data-event-calendar-next]');
      this.empty = section.querySelector('[data-event-calendar-empty]');
      this.loading = section.querySelector('[data-event-calendar-loading]');
      this.details = section.querySelector('[data-event-calendar-details]');
      this.fetchUrl = section.dataset.eventCalendarFetchUrl;
      this.addToCartLabel = section.dataset.eventCalendarAddLabel || '';
      this.showQuantitySelector = section.dataset.eventCalendarShowQuantity !== 'false';
      this.limit = Number.parseInt(section.dataset.eventCalendarLimit || '24', 10);
      this.eventElements = [];
      this.events = [];
      this.today = new Date();
      this.today.setHours(0, 0, 0, 0);
      this.currentMonth = startOfMonth(this.today);
      this.minMonth = this.currentMonth;
      this.maxMonth = this.currentMonth;

      if (!this.grid || !this.monthTitle || !this.prevButton || !this.nextButton || !this.details) return;

      this.monthTitle.textContent = monthFormatter.format(this.currentMonth);
      this.prevButton.disabled = true;
      this.nextButton.disabled = true;
      this.renderGrid();

      this.prevButton.addEventListener('click', () => this.changeMonth(-1));
      this.nextButton.addEventListener('click', () => this.changeMonth(1));

      this.loadEvents();
    }

    async loadEvents() {
      if (!this.fetchUrl) {
        this.setupEvents();
        return;
      }

      try {
        const response = await fetch(this.fetchUrl);

        if (!response.ok) throw new Error(`Event calendar request failed: ${response.status}`);

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const results = parsed.querySelector('[data-event-calendar-results]');

        if (results) {
          this.details.querySelectorAll(eventSelector).forEach((event) => {
            event.remove();
          });

          results.querySelectorAll(eventSelector).forEach((event, index) => {
            window.InkhavenEventControlIds(event, this.section, index);
            this.details.insertBefore(event, this.empty);
          });
        }
      } catch (error) {
        // Leave the empty state visible when search results cannot be loaded.
      } finally {
        if (this.loading) {
          this.loading.hidden = true;
        }

        this.setupEvents();
      }
    }

    setupEvents() {
      this.eventElements = Array.from(this.section.querySelectorAll(eventSelector));
      this.applyEventControls();
      this.events = this.eventElements
        .map((element) => {
          const date = new Date(`${element.dataset.eventDate}T00:00:00`);

          return {
            id: element.dataset.eventId,
            title: element.dataset.eventTitle || '',
            date,
            dateKey: element.dataset.eventDate,
            monthKey: monthKey(date),
            available: element.dataset.eventAvailable === 'true',
            element
          };
        })
        .filter((event) => !Number.isNaN(event.date.getTime()))
        .sort((a, b) => a.date - b.date)
        .slice(0, Number.isNaN(this.limit) ? 24 : this.limit);

      if (this.events.length === 0) {
        this.currentMonth = startOfMonth(this.today);
        this.minMonth = this.currentMonth;
        this.maxMonth = this.currentMonth;
        this.selectedEvent = null;
        this.render();
        return;
      }

      this.minMonth = this.resolveMinMonth();
      this.maxMonth = startOfMonth(this.events[this.events.length - 1].date);
      this.currentMonth = this.resolveDefaultMonth();
      this.selectedEvent = this.resolveDefaultEvent();
      this.render();
    }

    resolveMinMonth() {
      const previousMonth = addMonths(this.today, -1);
      const firstEventMonth = startOfMonth(this.events[0].date);

      return compareMonths(firstEventMonth, previousMonth) > 0 ? firstEventMonth : previousMonth;
    }

    resolveDefaultMonth() {
      const currentMonthKey = monthKey(this.today);
      const hasUpcomingInCurrentMonth = this.events.some(
        (event) => event.monthKey === currentMonthKey && event.date >= this.today
      );

      if (hasUpcomingInCurrentMonth) return startOfMonth(this.today);

      const nextEvent = this.events.find((event) => event.date >= this.today);

      if (nextEvent) return startOfMonth(nextEvent.date);

      return this.minMonth;
    }

    resolveDefaultEvent() {
      const upcomingInMonth = this.events.find(
        (event) => event.monthKey === monthKey(this.currentMonth) && event.date >= this.today
      );

      if (upcomingInMonth) return upcomingInMonth;

      return this.events.find((event) => event.monthKey === monthKey(this.currentMonth)) || this.events[0];
    }

    changeMonth(direction) {
      const nextMonth = addMonths(this.currentMonth, direction);

      if (compareMonths(nextMonth, this.minMonth) < 0 || compareMonths(nextMonth, this.maxMonth) > 0) return;

      this.currentMonth = nextMonth;
      this.selectedEvent = this.events.find((event) => event.monthKey === monthKey(this.currentMonth)) || this.selectedEvent;
      this.render();
    }

    render() {
      this.monthTitle.textContent = monthFormatter.format(this.currentMonth);
      this.prevButton.disabled = compareMonths(addMonths(this.currentMonth, -1), this.minMonth) < 0;
      this.nextButton.disabled = compareMonths(addMonths(this.currentMonth, 1), this.maxMonth) > 0;
      this.renderGrid();
      this.renderDetails();
    }

    renderGrid() {
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();
      const firstDay = new Date(year, month, 1);
      const firstGridDate = new Date(year, month, 1 - firstDay.getDay());
      const selectedEventId = this.selectedEvent && this.selectedEvent.id;
      const fragment = document.createDocumentFragment();

      this.grid.innerHTML = '';

      for (let index = 0; index < 42; index += 1) {
        const date = new Date(firstGridDate);
        date.setDate(firstGridDate.getDate() + index);

        const dateKey = isoDate(date);
        const dayEvents = this.events.filter((event) => event.dateKey === dateKey);
        const eventToSelect = dayEvents.find((event) => event.available) || dayEvents[0];
        const element = document.createElement(dayEvents.length > 0 ? 'button' : 'span');

        element.className = 'event-calendar__day';
        element.textContent = date.getDate();

        if (date.getMonth() !== month) {
          element.classList.add('is-outside');
        }

        if (dayEvents.length > 0) {
          element.type = 'button';
          element.classList.add('has-event');
          element.setAttribute('aria-label', `${dateKey}: ${dayEvents.map((event) => event.title).join(', ')}`);

          if (dayEvents.some((event) => event.available)) {
            element.classList.add('has-available-event');
          } else {
            element.classList.add('has-disabled-event');
          }

          if (dayEvents.some((event) => event.id === selectedEventId)) {
            element.classList.add('is-selected');
          }

          element.addEventListener('click', () => {
            this.selectedEvent = eventToSelect;
            this.render();
          });
        }

        fragment.appendChild(element);
      }

      this.grid.appendChild(fragment);
    }

    renderDetails() {
      this.eventElements.forEach((element) => {
        element.hidden = true;
      });

      if (!this.selectedEvent) {
        this.empty && (this.empty.hidden = false);
        return;
      }

      this.empty && (this.empty.hidden = true);
      this.selectedEvent.element.hidden = false;
      this.applyDescriptionLinkFallback(this.selectedEvent.element);
    }

    applyDescriptionLinkFallback(eventElement) {
      const description = eventElement.querySelector('[data-event-calendar-description]');
      const fallbackLink = eventElement.querySelector('[data-event-calendar-description-link]');

      if (!description || !fallbackLink) {
        return;
      }

      const isTruncated = description.scrollHeight > description.clientHeight + 0.5;
      fallbackLink.hidden = !isTruncated;
    }

    applyEventControls() {
      this.eventElements.forEach((element) => {
        const addButton = element.querySelector('[data-event-calendar-add-button]');
        const variantSelect = element.querySelector('[data-event-calendar-variant-select]');
        const quantityInput = element.querySelector('[data-event-calendar-quantity-input], input[name="quantity"]');

        if (this.addToCartLabel && addButton) {
          addButton.dataset.eventCalendarDefaultAddLabel = this.addToCartLabel;
          addButton.dataset.eventCalendarCartLabel = this.addToCartLabel;
        }

        if (variantSelect && !variantSelect.hasAttribute('data-event-calendar-variant-bound')) {
          variantSelect.setAttribute('data-event-calendar-variant-bound', 'true');
          variantSelect.addEventListener('change', () => this.syncEventPurchaseControls(element));
        }

        if (quantityInput && !quantityInput.hasAttribute('data-event-calendar-quantity-change-bound')) {
          quantityInput.setAttribute('data-event-calendar-quantity-change-bound', 'true');
          quantityInput.addEventListener('change', () => this.syncEventPurchaseControls(element));
        }

        if (!this.showQuantitySelector) {
          const quantityControl = element.querySelector('.event-calendar__quantity-control');
          const quantityLabel = element.querySelector('.event-calendar__quantity-label');
          const form = element.querySelector('.event-calendar__form');

          quantityControl && quantityControl.remove();
          quantityLabel && quantityLabel.remove();

          if (form && !form.querySelector('input[name="quantity"]')) {
            const quantityInput = document.createElement('input');
            quantityInput.type = 'hidden';
            quantityInput.name = 'quantity';
            quantityInput.value = '1';

            if (element.dataset.eventAvailable !== 'true') {
              quantityInput.disabled = true;
            }

            form.insertBefore(quantityInput, addButton);
          }

          this.syncEventPurchaseControls(element);
          return;
        }

        element.querySelectorAll('[data-event-calendar-quantity-decrease], [data-event-calendar-quantity-increase]').forEach((button) => {
          if (button.hasAttribute('data-event-calendar-quantity-bound')) return;

          button.setAttribute('data-event-calendar-quantity-bound', 'true');
          button.addEventListener('click', () => {
            const control = button.closest('.event-calendar__quantity-control');
            const input = control && control.querySelector('[data-event-calendar-quantity-input]');

            if (!input || input.disabled) return;

            const currentValue = Number.parseInt(input.value, 10) || 1;
            const nextValue = button.hasAttribute('data-event-calendar-quantity-increase')
              ? currentValue + 1
              : Math.max(1, currentValue - 1);

            input.value = String(nextValue);
            this.syncEventPurchaseControls(element);
          });
        });

        this.syncEventPurchaseControls(element);
      });
    }

    syncEventPurchaseControls(element) {
      const form = element.querySelector('.event-calendar__form');
      const variantSelect = element.querySelector('[data-event-calendar-variant-select]');
      const variantInput = element.querySelector('[data-event-calendar-variant-input]');
      const selectedOption = variantSelect && variantSelect.selectedOptions[0];
      const variantId = variantSelect ? variantSelect.value : variantInput && variantInput.value;
      const quantityInput = form && form.querySelector('input[name="quantity"]');
      const quantity = Math.max(Number.parseInt(quantityInput && quantityInput.value, 10) || 1, 1);
      const isFree = selectedOption
        ? selectedOption.dataset.eventCalendarFree === 'true'
        : element.dataset.eventSelectedFree === 'true';
      const isAvailable = selectedOption ? !selectedOption.disabled : element.dataset.eventAvailable === 'true';
      const price = element.querySelector('[data-event-calendar-price]');
      const rsvpButton = element.querySelector('[data-event-calendar-rsvp]');
      const addButton = element.querySelector('[data-event-calendar-add-button]');

      if (price && selectedOption && selectedOption.dataset.eventCalendarPriceText) {
        price.textContent = selectedOption.dataset.eventCalendarPriceText;
      }

      if (rsvpButton) {
        rsvpButton.hidden = !isFree || !isAvailable || !variantId;

        if (variantId && rsvpButton.dataset.eventCalendarRsvpBase) {
          rsvpButton.href = `${rsvpButton.dataset.eventCalendarRsvpBase}${encodeURIComponent(variantId)}:${quantity}`;
        }
      }

      if (addButton) {
        addButton.disabled = !isAvailable;
        addButton.classList.toggle('button--primary', !isFree);
        addButton.classList.toggle('button--secondary', isFree);
        addButton.textContent = !isAvailable
          ? addButton.dataset.eventCalendarSoldOutLabel
          : isFree
            ? addButton.dataset.eventCalendarCartLabel
            : addButton.dataset.eventCalendarDefaultAddLabel;
      }
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(sectionSelector).forEach((section) => {
      if (section.hasAttribute('data-event-calendar-initialized')) return;

      section.setAttribute('data-event-calendar-initialized', 'true');
      new EventCalendar(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });
})();
